import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useFolderStore } from "./folderStore";
import { useEmailStore } from "./emailStore";
import { useLabelStore } from "./labelStore";
import { refreshFolders } from "../utils/refreshFolders";
import { setActiveOfflineAccount } from "../utils/offlineStorage";
import { getApiBaseUrl } from "../utils/apiConfig";

export interface UserAccount {
  id: string;
  email: string;
  displayName: string;
  domain: string;
  avatar?: string;
  quotaBytes?: number;
  usedBytes?: number;
  token: string;
  refreshToken?: string;
  expiresAt?: number;
  lastActive?: number;
}

interface AuthState {
  user: UserAccount | null;
  token: string | null;
  accounts: UserAccount[];
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  addAccount: (email: string, password: string) => Promise<void>;
  switchAccount: (email: string) => Promise<void>;
  removeAccount: (email: string) => void;
  logout: () => void;
  logoutAll: () => void;
  refreshAccessToken: () => Promise<boolean>;
  setUser: (user: UserAccount) => void;
  setToken: (token: string) => void;
  clearError: () => void;
  fetchMe: () => Promise<void>;
  updateQuota: (usedBytes: number, quotaBytes: number) => void;
}

// Guard against rapid successive logout() calls (e.g., two 401s arriving within ms).
// After a logout, ignore subsequent calls for 2 seconds.
let lastLogoutAt = 0;
const LOGOUT_COOLDOWN_MS = 2000;

// Evita refinanciar tokens en paralelo (varios 401 simultáneos)
let inFlightRefresh: Promise<boolean> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      accounts: [],
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${getApiBaseUrl()}/mail/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
          });

          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.error || "Credenciales inválidas");
          }

          const data = await response.json();
          const newAccount: UserAccount = {
            id: data.user.email,
            email: data.user.email,
            displayName: data.user.displayName || data.user.email.split("@")[0],
            domain: data.user.domain,
            token: data.access_token,
            refreshToken: data.refresh_token,
            expiresAt: Date.now() + (data.expires_in || 4 * 3600) * 1000,
            lastActive: Date.now(),
          };

          const currentAccounts = get().accounts || [];
          const exists = currentAccounts.some((a) => a.email.toLowerCase() === newAccount.email.toLowerCase());
          const updatedAccounts = exists
            ? currentAccounts.map((a) =>
                a.email.toLowerCase() === newAccount.email.toLowerCase() ? newAccount : a
              )
            : [...currentAccounts, newAccount];

          set({
            user: newAccount,
            token: data.access_token,
            accounts: updatedAccounts,
            isLoading: false,
          });
        } catch (err) {
          set({
            isLoading: false,
            error: err instanceof Error ? err.message : "Error al iniciar sesión",
          });
          throw err;
        }
      },

      addAccount: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${getApiBaseUrl()}/mail/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
          });

          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.error || "Credenciales inválidas para esta cuenta");
          }

          const data = await response.json();
          const newAccount: UserAccount = {
            id: data.user.email,
            email: data.user.email,
            displayName: data.user.displayName || data.user.email.split("@")[0],
            domain: data.user.domain,
            token: data.access_token,
            refreshToken: data.refresh_token,
            expiresAt: Date.now() + (data.expires_in || 4 * 3600) * 1000,
            lastActive: Date.now(),
          };

          const currentAccounts = get().accounts || [];
          const filtered = currentAccounts.filter(
            (a) => a.email.toLowerCase() !== newAccount.email.toLowerCase()
          );

          // Add new account and immediately switch to it
          set({
            user: newAccount,
            token: data.access_token,
            accounts: [...filtered, newAccount],
            isLoading: false,
          });

          // Reset stores and refresh for the new account
          useFolderStore.getState().reset();
          useEmailStore.getState().reset();
          useLabelStore.getState().reset();
          setTimeout(() => {
            refreshFolders(data.access_token);
          }, 50);
        } catch (err) {
          set({
            isLoading: false,
            error: err instanceof Error ? err.message : "Error al vincular cuenta",
          });
          throw err;
        }
      },

      switchAccount: async (targetEmail: string) => {
        const { accounts, user } = get();
        if (user && user.email.toLowerCase() === targetEmail.toLowerCase()) return;

        const target = accounts.find((a) => a.email.toLowerCase() === targetEmail.toLowerCase());
        if (!target) return;

        // Update active user and active token
        set({
          user: { ...target, lastActive: Date.now() },
          token: target.token,
          error: null,
        });

        // Reset stores and reload folders/emails for the new active account
        useFolderStore.getState().reset();
        useEmailStore.getState().reset();
        useLabelStore.getState().reset();

        setTimeout(() => {
          refreshFolders(target.token);
          // Force email re-fetch for the new account
          useEmailStore.getState().triggerRefresh();
        }, 50);
      },

      removeAccount: (emailToRemove: string) => {
        const { accounts, user } = get();
        const updatedAccounts = accounts.filter(
          (a) => a.email.toLowerCase() !== emailToRemove.toLowerCase()
        );

        if (user && user.email.toLowerCase() === emailToRemove.toLowerCase()) {
          // If current active account was removed, switch to another or log out
          if (updatedAccounts.length > 0) {
            const next = updatedAccounts[0];
            set({
              user: next,
              token: next.token,
              accounts: updatedAccounts,
            });
            useFolderStore.getState().reset();
            useEmailStore.getState().reset();
            useLabelStore.getState().reset();
            setTimeout(() => {
              refreshFolders(next.token);
            }, 50);
          } else {
            set({ user: null, token: null, accounts: [] });
          }
        } else {
          set({ accounts: updatedAccounts });
        }
      },

      logout: () => {
        // Guard: prevent cascade — if logout was called recently, skip
        const now = Date.now();
        if (now - lastLogoutAt < LOGOUT_COOLDOWN_MS) return;
        lastLogoutAt = now;

        const { accounts, user } = get();
        if (!user) {
          set({ user: null, token: null, accounts: [], error: null });
          return;
        }

        // Find the next account to switch to (keep ALL accounts in the list —
        // logout on401 should NOT remove accounts, only switch to the next one).
        // Accounts are only removed explicitly via removeAccount() or logoutAll().
        const otherAccounts = accounts.filter(
          (a) => a.email.toLowerCase() !== user.email.toLowerCase()
        );

        if (otherAccounts.length > 0) {
          const next = otherAccounts[0];
          set({
            user: next,
            token: next.token,
            accounts: accounts, // Preserve ALL accounts
            error: null,
          });
          useFolderStore.getState().reset();
          useEmailStore.getState().reset();
          useLabelStore.getState().reset();
          setTimeout(() => {
            refreshFolders(next.token);
          }, 50);
        } else {
          set({ user: null, token: null, accounts: accounts, error: null });
        }
      },

      logoutAll: () => {
        set({ user: null, token: null, accounts: [], error: null });
        useFolderStore.getState().reset();
        useEmailStore.getState().reset();
        useLabelStore.getState().reset();
      },

      setUser: (user: UserAccount) => set({ user }),
      setToken: (token: string) => set({ token }),
      clearError: () => set({ error: null }),

      refreshAccessToken: async () => {
        if (inFlightRefresh) return inFlightRefresh;

        const doRefresh = async (): Promise<boolean> => {
          const state = get();
          const account = state.user;
          if (!account?.refreshToken) return false;
          try {
            const res = await fetch(`${getApiBaseUrl()}/mail/auth/refresh`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ refresh_token: account.refreshToken }),
            });
            if (!res.ok) return false;
            const data = await res.json();
            const now = Date.now();
            const refreshed: UserAccount = {
              ...account,
              token: data.access_token,
              refreshToken: data.refresh_token || account.refreshToken,
              expiresAt: now + (data.expires_in || 4 * 3600) * 1000,
              lastActive: now,
            };
            const updatedAccounts = get().accounts.map((a) =>
              a.email.toLowerCase() === account.email.toLowerCase() ? refreshed : a
            );
            set({ user: refreshed, token: refreshed.token, accounts: updatedAccounts });
            return true;
          } catch {
            return false;
          }
        };

        inFlightRefresh = doRefresh();
        try {
          return await inFlightRefresh;
        } finally {
          inFlightRefresh = null;
        }
      },

      fetchMe: async () => {
        const state = get();
        if (!state.token) return;
        try {
          const res = await fetch(`${getApiBaseUrl()}/mail/auth/me`, {
            headers: { Authorization: `Bearer ${state.token}` },
          });
          if (!res.ok) return;
          const data = await res.json();
          set((prev) => ({
            user: prev.user
              ? {
                  ...prev.user,
                  displayName: data.displayName || prev.user.displayName,
                  quotaBytes: data.quotaBytes,
                  usedBytes: data.usedBytes,
                }
              : prev.user,
          }));
        } catch {
          // silent
        }
      },

      updateQuota: (usedBytes: number, quotaBytes: number) =>
        set((prev) => ({
          user: prev.user ? { ...prev.user, usedBytes, quotaBytes } : prev.user,
        })),
    }),
    {
      name: "magicmail-webmail-auth",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        accounts: state.accounts,
      }),
    },
  ),
);

// Mantener la caché offline aislada por cuenta activa
useAuthStore.subscribe((state, prev) => {
  if (state.user?.email !== prev.user?.email) {
    setActiveOfflineAccount(state.user?.email ?? null);
  }
});
